import {getSDEParamsForAsyncType, getSDEParamsForHostedScraperType} from '@scraperapi/scraperapi-sde-descriptor-lib';
import { AsyncUrls } from "./urls";
import { AmazonProductContent } from "./amazon-product"
import { AmazonSearchContent } from "./amazon-search"
import { UnknownContent } from "./unknown";
import { ProjectContentAP, ProjectContentHS, SDEContent, ProjectIcon } from "./sdecontentTypes";
import { CollectorType } from "providers/HostedScrapingProvider";
import { AmazonOfferContent } from "./amazon-offer";
import { AmazonReviewContent } from "./amazon-review";
import { GoogleJobsContent } from "./google-jobs";
import { GoogleNewsContent } from "./google-news";
import { GoogleSearchContent } from "./google-search";
import { GoogleShoppingContent } from "./google-shopping";
import { GoogleMapsSearchContent } from "./google-mapssearch";
import { WalmartProductContent } from "./walmart-product";
import { WalmartCategoryContent } from "./walmart-category";
import { WalmartSearchContent } from "./walmart-search";
import { WalmartReviewContent } from "./walmart-review";
import { EbayProductContent } from "./ebay-product";
import { EbaySearchContent } from "./ebay-search";
import { HomeDepotSearchContent } from "./homedepot-search";
import { HomeDepotProductContent } from "./homedepot-product";
import { HomeDepotReviewsContent } from "./homedepot-reviews";
import { RedfinForSaleContent } from "./redfin-for-sale";
import { RedfinForRentContent } from "./redfin-for-rent";
import { RedfinSearch } from "./redfin-search";
import { RedfinAgent } from './redfin-agent';
import { LlmUrls } from './llm-urls';
import { ChatGPTContent } from "./chatgpt";
import { PerplexityContent } from "./perplexity";
import { GoogleGeminiContent } from "./google-gemini";
import { GoogleAiModeContent } from "./google-ai-mode";


const allProjectDescriptors = [
  AsyncUrls, LlmUrls,
  AmazonProductContent, AmazonSearchContent, AmazonOfferContent, AmazonReviewContent,
  GoogleJobsContent, GoogleNewsContent, GoogleSearchContent, GoogleShoppingContent, GoogleMapsSearchContent,
  WalmartProductContent, WalmartCategoryContent, WalmartSearchContent, WalmartReviewContent,
  EbayProductContent, EbaySearchContent,
  HomeDepotSearchContent, HomeDepotProductContent, HomeDepotReviewsContent,
  RedfinForSaleContent, RedfinForRentContent, RedfinSearch, RedfinAgent,
  ChatGPTContent, PerplexityContent, GoogleGeminiContent, GoogleAiModeContent
];

export const validCollectorTypes: CollectorType[] = allProjectDescriptors.map(projectType => projectType.projectType);

export const getSDEDescriptorForProjectType = (projectType: CollectorType | undefined): SDEContent => {
  const result = allProjectDescriptors.find(sde => sde.projectType === projectType);
  return result ?? UnknownContent;
}

export const getSDEPropertiesSchema = (value: string) => {
  if(value === 'async_urls' || value === 'async_urls_llm') return getSDEParamsForAsyncType(value)?.propertiesJsonSchemaPart;
  else return getSDEParamsForHostedScraperType(value)?.propertiesJsonSchemaPart
};

const SDE_OUTPUT_FORMAT_DISPLAY_ORDER = [ 'json', 'csv', 'html' ];

export const sdeOutputFormatLabels: Record<string, string> = { json: 'JSON', csv: 'CSV', html: 'HTML' };

export const getSdeOutputFormats = (projectType: CollectorType): string[] => {
  const schema = getSDEPropertiesSchema(projectType) as { output_format?: { enum?: string[] } } | undefined;
  const declared = schema?.output_format?.enum ?? [ 'json', 'csv' ];
  return SDE_OUTPUT_FORMAT_DISPLAY_ORDER.filter(format => declared.includes(format));
};

export const isAsyncUrlsProject = (projectType: CollectorType): boolean => getSDEDescriptorForProjectType(projectType).projectCategory === 'async_urls';
export const isAsyncUrlsLlmProject = (projectType: CollectorType): boolean => getSDEDescriptorForProjectType(projectType).projectCategory === 'async_urls_llm';
export const isAmazonProject = (projectType: CollectorType): boolean => getSDEDescriptorForProjectType(projectType).projectCategory === 'amazon';
export const isGoogleProject = (projectType: CollectorType): boolean => getSDEDescriptorForProjectType(projectType).projectCategory === 'google';
export const isWalmartProject = (projectType: CollectorType): boolean => getSDEDescriptorForProjectType(projectType).projectCategory === 'walmart';
export const isEbayProject = (projectType: CollectorType): boolean => getSDEDescriptorForProjectType(projectType).projectCategory === 'ebay';
export const isRedfinProject = (projectType: CollectorType): boolean => getSDEDescriptorForProjectType(projectType).projectCategory === 'redfin';
export const isHomeDepotSearchProject = (projectType: CollectorType): boolean => projectType === 'async_homedepot_search';
export const isHomeDepotProductProject = (projectType: CollectorType): boolean => projectType === 'async_homedepot_product';
export const isHomeDepotReviewsProject = (projectType: CollectorType): boolean => projectType === 'async_homedepot_reviews';

export const aiResultsProjectTypes: CollectorType[] = [ 'async_chatgpt', 'async_perplexity', 'async_google_gemini', 'async_google_ai_mode' ];
export const isAiResultsProject = (projectType: CollectorType): boolean => aiResultsProjectTypes.includes(projectType);
export const isChatGptProject = (projectType: CollectorType): boolean => projectType === 'async_chatgpt';
export const isAiChatProject = (projectType: CollectorType): boolean =>
  projectType === 'async_chatgpt' || projectType === 'async_perplexity' || projectType === 'async_google_gemini';

// AI Results endpoints show a BETA badge for ~3 months post-launch (same window as the homepage "New" label).
const AI_RESULTS_BETA_UNTIL = new Date("2026-10-07").getTime();
export const isAiResultsBetaActive = (): boolean => Date.now() < AI_RESULTS_BETA_UNTIL;

export type SdeTrackingInfo = { sde_type: string; sde_name: string; is_ai_result: boolean };

export const getSdeTrackingInfo = (projectType: CollectorType): SdeTrackingInfo => {
  const descriptor = getSDEDescriptorForProjectType(projectType);
  return {
    sde_type: projectType,
    sde_name: descriptor.apiPlayground.dropdownTitle || descriptor.apiPlayground.projectContent.title,
    is_ai_result: isAiResultsProject(projectType),
  };
};

export const isSDEProject = (projectType: CollectorType): boolean => getSDEDescriptorForProjectType(projectType).projectCategory !== 'async_urls' && getSDEDescriptorForProjectType(projectType).projectCategory !== 'async_urls_llm';

export const sdeDescriptors = allProjectDescriptors.filter((type) => (type.projectType !== 'async_urls' && type.projectType !== 'async_amazon_review' && type.projectType !== 'async_urls_llm'))
export const validSDECollectorTypes = validCollectorTypes.filter((type) => (type !== 'async_urls' && type !== 'async_amazon_review'))

const sdeGroups: { header: string, match: (category: string, type: CollectorType) => boolean, beta?: boolean }[] = [
  { header: 'AI results endpoints', match: (_category, type) => aiResultsProjectTypes.includes(type), beta: true },
  { header: 'E-Commerce', match: (category) => [ 'amazon', 'ebay', 'walmart', 'homedepot' ].includes(category) },
  { header: 'Search', match: (category) => category === 'google' },
  { header: 'Real Estate', match: (category) => category === 'redfin' }
];

// Single source for the SDE dropdowns; uncategorised SDEs fall into a trailing headerless group.
export type GroupedSdeDescriptors = { header?: string, beta?: boolean, sdes: SDEContent[] };

export const groupedSdeDescriptors: GroupedSdeDescriptors[] = (() => {
  const placed = new Set<CollectorType>();
  const groups: GroupedSdeDescriptors[] = [];
  for (const group of sdeGroups) {
    const sdes = sdeDescriptors.filter(sde => !placed.has(sde.projectType) && group.match(sde.projectCategory, sde.projectType));
    if (sdes.length === 0) continue;
    sdes.forEach(sde => placed.add(sde.projectType));
    groups.push({ header: group.header, beta: group.beta, sdes });
  }
  const leftover = sdeDescriptors.filter(sde => !placed.has(sde.projectType));
  if (leftover.length > 0) groups.push({ sdes: leftover });
  return groups;
})();

export const sdeGroupedSelectOptions: { name: string, value: CollectorType, group?: string, beta?: boolean }[] =
  groupedSdeDescriptors.flatMap(group =>
    group.sdes.map(sde => ({ name: sde.apiPlayground.dropdownTitle, value: sde.projectType, group: group.header, beta: Boolean(group.beta) && isAiResultsBetaActive() }))
  );


// Validators ---------------------------------------------

export const inputValidatorFn = (projectType: string) => getSDEDescriptorForProjectType(projectType).validatorFunction;


// Project edit page ---------------------------------------------

export const projectTypeToTitleAndDescriptionHS = (projectType: CollectorType|undefined) => getSDEDescriptorForProjectType(projectType).newProjectEditProject.projectContent;

export const getInputBoxLabelsHS = (projectType: CollectorType|undefined) => getSDEDescriptorForProjectType(projectType).newProjectEditProject.inputBoxLabels;

export const getInputSectionLabelsHS = (projectType: CollectorType|undefined) => getSDEDescriptorForProjectType(projectType).newProjectEditProject.inputSectionLabels;

export const projectTypeToInputTitleHS = (projectType: CollectorType|undefined) => getSDEDescriptorForProjectType(projectType).newProjectEditProject.projectContent.inputTitle;

export const projectTypeHS = (projectType: CollectorType|undefined) => getSDEDescriptorForProjectType(projectType).newProjectEditProject.projectContent.title;

export const iconTypeHS = (projectType: CollectorType|undefined) => getSDEDescriptorForProjectType(projectType).newProjectEditProject.projectContent.icon;


// API playground ---------------------------------------------

export const projectTypeToTitleAndDescriptionAP = (projectType: CollectorType|undefined) => getSDEDescriptorForProjectType(projectType).apiPlayground.projectContent;

export const getInputSectionLabelsForAP = (projectType: CollectorType|undefined) => getSDEDescriptorForProjectType(projectType).apiPlayground.inputSectionLabels;

export const projectTypeAP = (projectType: CollectorType|undefined) => getSDEDescriptorForProjectType(projectType).apiPlayground.projectContent.title;

export const iconTypeAP = (projectType: CollectorType|undefined) => getSDEDescriptorForProjectType(projectType).apiPlayground.projectContent.icon;

export const enterInputsMessageAP = (projectType: CollectorType | undefined) => getSDEDescriptorForProjectType(projectType).apiPlayground.enterInputsMessage;

export const enterInputsPlaceholderAP = (projectType: CollectorType | undefined) => getSDEDescriptorForProjectType(projectType).apiPlayground.enterInputsPlaceholder;

export const codeViewPlaceHolderAP = (projectType: CollectorType | undefined) => getSDEDescriptorForProjectType(projectType).apiPlayground.codeViewPlaceHolder;

// Common -----------------------------------------------------

export const collectorTypeToInputFormat = (type: CollectorType, inputLength: number): string => {
  const descriptor = getSDEDescriptorForProjectType(type);
  if (inputLength === 1) {
    return descriptor.inputKey;
  } else {
    return descriptor.inputsKey;
  }
}

export const collectorTypeToAPIURL = (projectType: CollectorType) => getSDEDescriptorForProjectType(projectType).apiUrlFn;

export type ProjectVariant = 'hosted-scraper' | 'api-playground';

export const projectType = (variant: ProjectVariant, configType: CollectorType): string => {
  switch (variant) {
    case 'hosted-scraper': return projectTypeHS(configType);
    case 'api-playground': return projectTypeAP(configType);
  }
};

export const iconType = (variant: ProjectVariant, configType: CollectorType): ProjectIcon => {
  switch (variant) {
    case 'hosted-scraper': return iconTypeHS(configType);
    case 'api-playground': return iconTypeAP(configType);
  }
};

export const projectTypeToTitleAndDescription = (variant: ProjectVariant, projectType: CollectorType): ProjectContentHS | ProjectContentAP => {
  switch (variant) {
    case 'hosted-scraper': return projectTypeToTitleAndDescriptionHS(projectType);
    case 'api-playground': return projectTypeToTitleAndDescriptionAP(projectType);
  }
};
